import { useEffect, useState } from "react";
import api from "../lib/api";
import { Bell, FileWarning, ShieldAlert, Landmark, DollarSign, Wrench, Banknote, Home, Plug, CheckCircle2 } from "lucide-react";
import { DocLinks } from "../components/DocActions";

const ICON = { Lease: FileWarning, Insurance: ShieldAlert, "Property Tax": Landmark, "Late Rent": DollarSign, Maintenance: Wrench, Mortgage: Banknote, HOA: Home, Utility: Plug };
const TONE = {
  red: { wrap: "border-l-red-500 bg-red-50 dark:bg-red-500/10", text: "text-red-600", label: "Urgent" },
  orange: { wrap: "border-l-amber-500 bg-amber-50 dark:bg-amber-500/10", text: "text-amber-600", label: "Upcoming" },
  green: { wrap: "border-l-emerald-500 bg-emerald-50 dark:bg-emerald-500/10", text: "text-emerald-600", label: "Completed" },
  blue: { wrap: "border-l-blue-500 bg-blue-50 dark:bg-blue-500/10", text: "text-blue-600", label: "Info" },
};

export default function Alerts() {
  const [notes, setNotes] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {}); }, []);

  const counts = notes.reduce((a, n) => ({ ...a, [n.severity]: (a[n.severity] || 0) + 1 }), {});
  const filtered = filter === "all" ? notes : notes.filter((n) => n.severity === filter);

  return (
    <div className="space-y-6" data-testid="page-alerts">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Alert Center</h1>
        <p className="text-sm text-muted-foreground mt-1">{notes.length} alerts · color-coded by priority</p>
      </div>

      <div className="flex flex-wrap gap-3">
        {[["all", "All", notes.length, "bg-primary text-primary-foreground"],
          ["red", "Urgent", counts.red || 0, "bg-red-500 text-white"],
          ["orange", "Upcoming", counts.orange || 0, "bg-amber-500 text-white"],
          ["blue", "Info", counts.blue || 0, "bg-blue-500 text-white"],
          ["green", "Completed", counts.green || 0, "bg-emerald-500 text-white"]].map(([key, label, count, cls]) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`filter-${key}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filter === key ? cls : "bg-card border border-border hover:bg-muted"}`}>
            {label} <span className="ml-1 opacity-80">({count})</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((n) => {
          const Icon = ICON[n.type] || (n.severity === "green" ? CheckCircle2 : Bell);
          const t = TONE[n.severity] || TONE.blue;
          return (
            <div key={n.id} className={`rounded-xl border border-border border-l-4 ${t.wrap} p-4 flex items-start gap-3`} data-testid={`alert-${n.id}`}>
              <Icon className="w-5 h-5 mt-0.5 text-muted-foreground" strokeWidth={1.75} />
              <div className="flex-1">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{n.type} · {n.date}</p>
                {n.doc_id && <div className="mt-1.5"><DocLinks docId={n.doc_id} label={n.doc_label} /></div>}
              </div>
              <span className={`text-xs uppercase tracking-wider font-semibold ${t.text}`}>{t.label}</span>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-muted-foreground text-center py-12">No alerts in this category.</p>}
      </div>
    </div>
  );
}
